import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import '../styles/Questionnaire.css';

interface QuestionData {
  id: string;
  text: string;
  attribute_id: string;
  attribute_name: string;
  component_name: string;
  dimension_name: string;
  weight: number;
  answerOptions: {
    id: string;
    text: string;
    score: number;
    display_order: number;
  }[];
}

const Questionnaire: React.FC = () => {
  const { hasPermission } = useAuth();
  const [questions, setQuestions] = useState<QuestionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedComponent, setSelectedComponent] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<QuestionData | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<QuestionData | null>(null);
  const [attributes, setAttributes] = useState<any[]>([]);
  const [allComponents, setAllComponents] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    text: '',
    attributeId: '',
    answerOptions: [
      { text: 'Poor', score: 1 },
      { text: 'Fair', score: 2 },
      { text: 'Good', score: 3 },
      { text: 'Very Good', score: 4 },
      { text: 'Excellent', score: 5 }
    ]
  });

  const canModify = hasPermission('canManageSettings');

  // Load questions from API
  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const [questionsData, attributesData, componentsData] = await Promise.all([
        apiService.getQuestions(),
        apiService.getAttributes(),
        apiService.getComponents()
      ]);
      setQuestions(questionsData);
      setAttributes(attributesData);
      setAllComponents(componentsData || []);
    } catch (err) {
      setError('Failed to load questions. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  // Body scroll lock/unlock for modals
  useEffect(() => {
    const isModalOpen = showCreateModal || showEditModal || showDeleteConfirm;
    
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '15px'; // Prevent layout shift from scrollbar
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    };
  }, [showCreateModal, showEditModal, showDeleteConfirm]);

  // CRUD Functions
  const handleCreateQuestion = () => {
    setFormData({
      text: '',
      attributeId: '',
      answerOptions: [
        { text: 'Poor', score: 1 },
        { text: 'Fair', score: 2 },
        { text: 'Good', score: 3 },
        { text: 'Very Good', score: 4 },
        { text: 'Excellent', score: 5 }
      ]
    });
    setSelectedQuestion(null);
    setShowCreateModal(true);
  };

  const handleEditQuestion = (question: QuestionData) => {
    setSelectedQuestion(question);
    setFormData({
      text: question.text,
      attributeId: question.attribute_id,
      answerOptions: [...question.answerOptions]
    });
    setShowEditModal(true);
  };

  const handleDeleteQuestion = (question: QuestionData) => {
    setQuestionToDelete(question);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteQuestion = async () => {
    if (!questionToDelete) return;

    try {
      await apiService.deleteQuestion(questionToDelete.id);
      await loadQuestions();
      setShowDeleteConfirm(false);
      setQuestionToDelete(null);
    } catch (err) {
      setError('Failed to delete question. Please try again.');
    }
  };

  const handleSaveQuestion = async () => {
    if (!formData.text.trim()) {
      setError('Question text is required');
      return;
    }

    if (!formData.attributeId && !selectedQuestion) {
      setError('Please select an attribute');
      return;
    }

    try {
      if (selectedQuestion) {
        // Update existing question
        await apiService.updateQuestion(selectedQuestion.id, {
          text: formData.text,
          answerOptions: formData.answerOptions
        });
      } else {
        // Create new question
        await apiService.createQuestion({
          text: formData.text,
          attributeId: formData.attributeId,
          answerOptions: formData.answerOptions
        });
      }

      await loadQuestions();
      setShowEditModal(false);
      setShowCreateModal(false);
      setSelectedQuestion(null);
      setError(null);
    } catch (err) {
      setError('Failed to save question. Please try again.');
    }
  };

  const handleCloseModals = () => {
    setShowEditModal(false);
    setShowCreateModal(false);
    setShowDeleteConfirm(false);
    setSelectedQuestion(null);
    setQuestionToDelete(null);
    setError(null);
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAnswerOptionChange = (index: number, field: 'text' | 'score', value: string) => {
    const newOptions = [...formData.answerOptions];
    newOptions[index] = {
      ...newOptions[index],
      [field]: field === 'score' ? parseInt(value) || 1 : value
    };
    handleFormChange('answerOptions', newOptions);
  };

  const addAnswerOption = () => {
    const newOptions = [...formData.answerOptions];
    newOptions.push({
      text: '',
      score: newOptions.length + 1
    });
    handleFormChange('answerOptions', newOptions);
  };

  const removeAnswerOption = (index: number) => {
    const newOptions = formData.answerOptions.filter((_, i) => i !== index);
    handleFormChange('answerOptions', newOptions);
  };

  const getScoreColor = (score: number) => {
    const colors = {
      1: '#dc3545', // Red
      2: '#fd7e14', // Orange
      3: '#ffc107', // Yellow
      4: '#66bb6a', // Light Green
      5: '#2e7d32'  // Dark Green
    };
    return colors[score as keyof typeof colors] || '#6c757d';
  };

  // Get all components with question counts
  const components = allComponents.map(comp => ({
    name: comp.name,
    count: questions.filter(q => q.component_name === comp.name).length
  })).sort((a, b) => a.name.localeCompare(b.name));

  // Filter questions by selected component
  const filteredQuestions = questions.filter(q => {
    const componentMatch = !selectedComponent || q.component_name === selectedComponent;
    return componentMatch;
  });

  // Group questions by component
  const questionsByComponent = filteredQuestions.reduce((acc, question) => {
    const componentName = question.component_name;
    if (!acc[componentName]) {
      acc[componentName] = [];
    }
    acc[componentName].push(question);
    return acc;
  }, {} as Record<string, QuestionData[]>);

  const totalAnswerOptions = questions.reduce((sum, q) => sum + q.answerOptions.length, 0);

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__content">
            <div className="page-header__title-section">
              <h1 className="page-header__title">Questionnaire Setup</h1>
            </div>
          </div>
        </div>
        <div className="master-data-container">
          <div className="empty-state">
            <div className="empty-state-icon">⏳</div>
            <h3>Loading Questionnaire</h3>
            <p>Please wait while we fetch questions and assessment data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__content">
            <div className="page-header__title-section">
              <h1 className="page-header__title">Questionnaire Setup</h1>
            </div>
          </div>
        </div>
        <div className="master-data-container">
          <div className="empty-state">
            <div className="empty-state-icon">⚠️</div>
            <h3>Error Loading Questions</h3>
            <p>{error}</p>
            <div className="empty-state-actions">
              <button className="btn btn-primary" onClick={loadQuestions}>
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__content">
            <div className="page-header__title-section">
              <h1 className="page-header__title">Questionnaire Setup</h1>
            </div>
          </div>
        </div>
        <div className="master-data-container">
          <div className="empty-state">
            <div className="empty-state-icon">❓</div>
            <h3>No Questions Available</h3>
            <p>Questions need to be created from the Questions Management page first. Go there to create assessment questions for the APR framework.</p>
            <div className="empty-state-actions">
              <a href="/framework/questions" className="btn btn-primary">
                Go to Questions Management
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Questionnaire Setup</h1>
          </div>
          <div className="page-header__actions">
            {canModify && (
              <button
                className="btn btn-primary"
                onClick={handleCreateQuestion}
                disabled={loading}
              >
                ➕ Add Question
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="master-data-container">


      {/* Filters - Same style as PhaseEffortPlanning */}
      <div className="md-card" style={{ marginBottom: '16px', padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
          {/* Component Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
            <label style={{ fontSize: '14px', fontWeight: '600', minWidth: '90px' }}>Component:</label>
            <select
              value={selectedComponent}
              onChange={(e) => setSelectedComponent(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '360px',
                flex: 1,
                maxWidth: '500px'
              }}
              disabled={components.length === 0}
            >
              <option value="">All Components ({questions.length})</option>
              {components.map(component => (
                <option key={component.name} value={component.name}>
                  {component.name} ({component.count})
                </option>
              ))}
            </select>
            {selectedComponent && (
              <button
                onClick={() => setSelectedComponent('')}
                style={{
                  padding: '6px 10px',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Clear
              </button>
            )}
          </div>
          
          {/* Filter Results Counter and Clear All */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: 'auto' }}>
            <span style={{ fontSize: '12px', color: '#666' }}>
              {selectedComponent ?
                `Showing ${filteredQuestions.length} of ${questions.length} questions` :
                `${questions.length} questions`
              }
            </span>
            {selectedComponent && (
              <button
                onClick={() => {
                  setSelectedComponent('');
                }}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#2196f3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Clear All
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Questions Display */}
      <div className="questions-content">
        {Object.entries(questionsByComponent).map(([componentName, componentQuestions]) => (
          <div key={componentName} className="component-section">
            <h3 className="component-title">
              <span className="component-icon">🔧</span>
              {componentName}
              <span className="question-count">({componentQuestions.length} questions)</span>
            </h3>

            <div className="questions-grid">
              {componentQuestions.map(question => (
                <div key={question.id} className="question-card">
                  {/* Attribute Header - Fixed Height */}
                  <div style={{
                    marginBottom: '12px',
                    paddingBottom: '12px',
                    borderBottom: '2px solid #e0e0e0',
                    minHeight: '110px'
                  }}>
                    <div style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#666',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      ATTRIBUTE
                    </div>
                    <h4 className="attribute-name" style={{ marginBottom: '8px', minHeight: '40px' }}>
                      {question.attribute_name}
                    </h4>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <div style={{
                        backgroundColor: 'white',
                        border: '2px solid #000',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#000'
                      }}>
                        Weight: <span style={{
                          color: getScoreColor(question.weight),
                          fontSize: '16px',
                          fontWeight: '700',
                          marginLeft: '4px'
                        }}>{question.weight}</span>
                      </div>
                    </div>
                  </div>

                  {/* Question Text - Fixed Height */}
                  <div className="question-text" style={{ marginBottom: '12px', minHeight: '80px' }}>
                    <p>{question.text}</p>
                  </div>

                  {/* Answer Options - Fixed Starting Point */}
                  <div className="answer-options" style={{ flex: 1, display: 'flex', flexDirection: 'column', marginTop: '16px' }}>
                    <h5 style={{ marginBottom: '12px', marginTop: '0' }}>Answer Options ({question.answerOptions.length})</h5>
                    <div className="options-list" style={{ minHeight: '180px' }}>
                      {question.answerOptions
                        .sort((a, b) => a.display_order - b.display_order)
                        .map(option => (
                          <div key={option.id} className="option-item">
                            <div
                              className="score-badge"
                              style={{ backgroundColor: getScoreColor(option.score) }}
                            >
                              {option.score}
                            </div>
                            <span className="option-text">{option.text}</span>
                          </div>
                        ))}
                    </div>
                  </div>

                  {canModify && (
                    <div className="question-actions" style={{
                      display: 'flex',
                      gap: '8px',
                      justifyContent: 'flex-end',
                      marginTop: 'auto',
                      paddingTop: '12px'
                    }}>
                      <button
                        onClick={() => handleEditQuestion(question)}
                        style={{
                          padding: '6px 14px',
                          fontSize: '13px',
                          fontWeight: '500',
                          fontFamily: 'inherit',
                          backgroundColor: '#f5f5f5',
                          color: '#333',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = '#e0e0e0';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = '#f5f5f5';
                        }}
                      >
                        EDIT
                      </button>
                      <button
                        onClick={() => handleDeleteQuestion(question)}
                        style={{
                          padding: '6px 14px',
                          fontSize: '13px',
                          fontWeight: '500',
                          fontFamily: 'inherit',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = '#c82333';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = '#dc3545';
                        }}
                      >
                        DELETE
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {filteredQuestions.length === 0 && selectedComponent && (
          <div className="no-questions">
            <p>No questions found for the selected component: {selectedComponent}</p>
          </div>
        )}
      </div>

      {/* Create/Edit Question Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="modal-overlay" onClick={handleCloseModals}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            minWidth: '600px',
            maxWidth: '700px',
            maxHeight: '90vh',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#333' }}>
                {selectedQuestion ? 'Edit Question' : 'Create New Question'}
              </h2>
            </div>

            {/* Scrollable Form Content */}
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '24px' }}>
              {error && (
                <div style={{
                  backgroundColor: '#f8d7da',
                  color: '#721c24',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  fontSize: '14px'
                }}>
                  {error}
                </div>
              )}

              <form onSubmit={(e) => { e.preventDefault(); handleSaveQuestion(); }} id="question-form">
                {!selectedQuestion && (
                  <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="attributeId" style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#333',
                      marginBottom: '8px'
                    }}>
                      Attribute *
                    </label>
                    <select
                      id="attributeId"
                      value={formData.attributeId}
                      onChange={(e) => handleFormChange('attributeId', e.target.value)}
                      required
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">Select an attribute</option>
                      {attributes
                        .filter(attr => !questions.some(q => q.attribute_id === attr.id))
                        .map(attr => (
                          <option key={attr.id} value={attr.id}>
                            {attr.dimension_name} → {attr.component_name} → {attr.name}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                {selectedQuestion && (
                  <div style={{
                    backgroundColor: '#e3f2fd',
                    color: '#0d47a1',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '20px'
                  }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '500' }}>
                      Attribute: {selectedQuestion.attribute_name}
                    </h4>
                    <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
                      {selectedQuestion.dimension_name} → {selectedQuestion.component_name}
                    </p>
                  </div>
                )}

                <div style={{ marginBottom: '20px' }}>
                  <label htmlFor="questionText" style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333',
                    marginBottom: '8px'
                  }}>
                    Question Text *
                  </label>
                  <textarea
                    id="questionText"
                    value={formData.text}
                    onChange={(e) => handleFormChange('text', e.target.value)}
                    rows={3}
                    placeholder="Enter the assessment question"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '500', color: '#333' }}>
                    Answer Options
                  </h4>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {formData.answerOptions.map((option, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 12px',
                        backgroundColor: '#f3edf7',
                        borderRadius: '8px',
                        border: '1px solid #cab6cf'
                      }}>
                        <div style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: getScoreColor(option.score),
                          color: 'white',
                          fontWeight: '600',
                          fontSize: '13px',
                          flexShrink: 0
                        }}>
                          {option.score}
                        </div>
                        <input
                          type="text"
                          placeholder="Answer text"
                          value={option.text}
                          onChange={(e) => handleAnswerOptionChange(index, 'text', e.target.value)}
                          required
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '14px',
                            backgroundColor: 'white'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </form>
            </div>

            {/* Fixed buttons at bottom */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                type="button"
                onClick={handleCloseModals}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                  e.currentTarget.style.borderColor = '#999';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = '#ddd';
                }}
                style={{
                  backgroundColor: 'transparent',
                  color: '#333',
                  border: '1px solid #ddd',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="question-form"
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#1565c0';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#1976d2';
                }}
                style={{
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
              >
                {selectedQuestion ? 'Update Question' : 'Create Question'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && questionToDelete && (
        <div className="form-modal">
          <div className="form-modal-content modal-small">
            <div className="form-modal-header">
              <h2>Delete Question</h2>
              <button className="btn-close" onClick={handleCloseModals} aria-label="Close form">×</button>
            </div>
            
            <div className="modal-body">
              <div className="delete-confirmation">
                <p>Are you sure you want to delete this question?</p>
                <div className="question-preview">
                  <strong>{questionToDelete.attribute_name}</strong>
                  <p>"{questionToDelete.text}"</p>
                </div>
                <p className="warning-text">This action cannot be undone.</p>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={handleCloseModals}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmDeleteQuestion}>
                Delete Question
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Questionnaire;